import { FormEvent, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Button, Checkbox, CommandBarButton, DefaultButton, Dialog, FontIcon, Stack, Text, Dropdown, IDropdownOption, ContextualMenu, IContextualMenuProps } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import { CheckmarkRegular, Copy20Filled, ArrowDownload20Filled, ThumbDislike20Filled, ThumbLike20Filled } from '@fluentui/react-icons'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import supersub from 'remark-supersub'
import { AskResponse, blobSAStoken, BotResponse, Citation, CosmosDBStatus, Feedback, historyMessageFeedback } from '../../api'
import { XSSAllowTags } from '../../constants/xssAllowTags'
import { AppStateContext } from '../../state/AppProvider'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import { parseAnswer } from './AnswerParser'
import 'katex/dist/katex.min.css';
import styles from './Answer.module.css'
import { exportOptions } from '../../utils/pptxExportHandlers';
import rehypeRaw from 'rehype-raw';
import './adaptive-table.css';
import { TrafoChatReferences } from './components/TrafoChatReferences'
import { GeneralReferences } from './components/GeneralReferences'
import { FeatureFlag, agentHasFeature } from '#/constants/agentFeatures'


interface Props {
  answer: AskResponse;
  onCitationClicked: (citedDocument: Citation) => void;
  messages: BotResponse[];
  selectedAgent:string

}

export const Answer: React.FC<Props> = ({ answer, onCitationClicked, messages ,selectedAgent}) => {
  const [isBlinking, setIsBlinking] = useState(false);

  const initializeAnswerFeedback = (answer: AskResponse) => {
    if (answer.message_id == undefined) return undefined;
    if (answer.feedback == undefined) return undefined;
    if (answer.feedback.split(',').length > 1) return Feedback.Negative;
    if (Object.values(Feedback).includes(answer.feedback)) return answer.feedback;
    return Feedback.Neutral;
  };

  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false)

  const parsedAnswer = useMemo(() => parseAnswer(answer, agentHasFeature(FeatureFlag.IncludeAllCitations, selectedAgent)), [answer])
  const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen)
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer))
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [showReportInappropriateFeedback, setShowReportInappropriateFeedback] = useState(false)
  const [showcopiedStatus, setshowcopiedStatus] = useState(false)
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>([])
  const appStateContext = useContext(AppStateContext)
  const FEEDBACK_ENABLED = appStateContext?.state.frontendSettings?.feedback_enabled && appStateContext?.state.isCosmosDBAvailable?.cosmosDB
  const SANITIZE_ANSWER = appStateContext?.state.frontendSettings?.sanitize_answer
  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded)
    toggleIsRefAccordionOpen()
  }

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen)
  }, [isRefAccordionOpen])

  useEffect(() => {
    if (answer.message_id == undefined) return

    let currentFeedbackState
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) {
      currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id]
    } else {
      currentFeedbackState = initializeAnswerFeedback(answer)
    }
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, answer.message_id])
  

  
  const onLikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    // Set or unset the thumbs up state
    if (feedbackState == Feedback.Positive) {
      newFeedbackState = Feedback.Neutral
    } else {
      newFeedbackState = Feedback.Positive
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
    setFeedbackState(newFeedbackState)

    const userId = localStorage.getItem('userId');
    const email = localStorage.getItem('email');
    const accessToken = localStorage.getItem('accessToken');
    const conversationId = localStorage.getItem('conversationId') ?? '';

    await historyMessageFeedback(answer.message_id, newFeedbackState, email!, userId!, accessToken!, conversationId, answer.order_id)
  }

  const onDislikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
      newFeedbackState = Feedback.Negative
      setFeedbackState(newFeedbackState)
      setIsFeedbackDialogOpen(true)
    } else {
      // Reset negative feedback to neutral
      newFeedbackState = Feedback.Neutral
      setFeedbackState(newFeedbackState)

      const userId = localStorage.getItem('userId');
      const email = localStorage.getItem('email');
      const accessToken = localStorage.getItem('accessToken');
      const conversationId = localStorage.getItem('conversationId') ?? '';
      await historyMessageFeedback(answer.message_id, Feedback.Neutral, email!, userId!, accessToken!, conversationId, answer.order_id)
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
  }

  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    if (answer.message_id == undefined) return
    const selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback

    let feedbackList = negativeFeedbackList.slice()
    if (checked) {
      feedbackList.push(selectedFeedback)
    } else {
      feedbackList = feedbackList.filter(f => f !== selectedFeedback)
    }

    setNegativeFeedbackList(feedbackList)
  }

  const onSubmitNegativeFeedback = async () => {
    if (answer.message_id == undefined)
      return

    const userId = localStorage.getItem('userId');
    const email = localStorage.getItem('email');
    const accessToken = localStorage.getItem('accessToken');
    const conversationId = localStorage.getItem('conversationId') ?? '';
    await historyMessageFeedback(answer.message_id, negativeFeedbackList.join(','), email!, userId!, accessToken!, conversationId, answer.order_id)
    resetFeedbackDialog()
  }

  const resetFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false)
    setShowReportInappropriateFeedback(false)
    setNegativeFeedbackList([])
  }

  const UnhelpfulFeedbackContent = () => {
    return (
      <>
        <div>Why wasn't this response helpful?</div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="Citations are missing"
            id={Feedback.MissingCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.MissingCitation)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Citations are wrong"
            id={Feedback.WrongCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.WrongCitation)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="The response is not from my data"
            id={Feedback.OutOfScope}
            defaultChecked={negativeFeedbackList.includes(Feedback.OutOfScope)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Inaccurate or irrelevant"
            id={Feedback.InaccurateOrIrrelevant}
            defaultChecked={negativeFeedbackList.includes(Feedback.InaccurateOrIrrelevant)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherUnhelpful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherUnhelpful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
        <div onClick={() => setShowReportInappropriateFeedback(true)} style={{ color: '#115EA3', cursor: 'pointer' }}>
          Report inappropriate content
        </div>
      </>
    )
  }

  async function downloadFileFromPath(citation: Citation): Promise<void> {

      var filepath=citation.url;

      const link = document.createElement('a');
      link.href=filepath;
      
      
      link.setAttribute('download',citation.filepath);
      link.target="_blank";
      document.body.appendChild(link);

      // Start download
      link.click();

      console.log(citation.url)
    }
  

  const onCopyMessageClicked = async () => {
    if (answer.message_id == undefined) return

    const textarea = document.createElement('textarea')
    textarea.value = answer.answer
    textarea.style.position = 'fixed'
    textarea.style.top = '0'
    textarea.style.left = '0'
    textarea.style.opacity = '0'
    textarea.style.pointerEvents = 'none'
    document.body.appendChild(textarea)
    
    try {
      textarea.select()
      document.execCommand('copy')
    } catch (err) {
      console.error('Copy failed:', err)
    } finally {
      document.body.removeChild(textarea)
    }
    
    setshowcopiedStatus(true)
    setTimeout(() => setshowcopiedStatus(false), 2000)
  }

  const ReportInappropriateFeedbackContent = () => {
    return (
      <>
        <div>
          The content is <span style={{ color: 'red' }}>*</span>
        </div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="Hate speech, stereotyping, demeaning"
            id={Feedback.HateSpeech}
            defaultChecked={negativeFeedbackList.includes(Feedback.HateSpeech)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Violent: glorification of violence, self-harm"
            id={Feedback.Violent}
            defaultChecked={negativeFeedbackList.includes(Feedback.Violent)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Sexual: explicit content, grooming"
            id={Feedback.Sexual}
            defaultChecked={negativeFeedbackList.includes(Feedback.Sexual)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Manipulative: devious, emotional, pushy, bullying"
            defaultChecked={negativeFeedbackList.includes(Feedback.Manipulative)}
            id={Feedback.Manipulative}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherHarmful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherHarmful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
      </>
    )
  }


  const components: Components = {
    code({ node, ...props }: { node: any;[key: string]: any }): ReactElement {
      let language;
      if (props.className) {
        const match = props.className.match(/language-(\w+)/);
        language = match ? match[1] : undefined;
      }
      const codeString = node.children[0].value ?? '';
      return (
        <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
          {codeString}
        </SyntaxHighlighter>
      );
    },    
    table({ node, ...props }: { node: any;[key: string]: any }): ReactElement {
      return <table className="ac-table" {...props} />;
    },
    th({ node, ...props }: { node: any;[key: string]: any }): ReactElement {
      return <th className="ac-table-header" {...props} />;
    },
    td({ node, ...props }: { node: any;[key: string]: any }): ReactElement {
      return <td className="ac-table-cell" {...props} />;
    },
  };

  return (
    <>
      <Stack className={styles.answerContainer} tabIndex={0}>
      <Stack>
        <Stack.Item>
          <Stack horizontal grow>
              <Stack.Item grow>
                { agentHasFeature(FeatureFlag.RemarkMath, selectedAgent) ? <ReactMarkdown
                  linkTarget="_blank"
                  remarkPlugins={[remarkGfm, supersub, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  children={
                    SANITIZE_ANSWER
                      ? DOMPurify.sanitize(parsedAnswer.markdownFormatText ?? '', { ALLOWED_TAGS: XSSAllowTags })
                      : parsedAnswer.markdownFormatText ?? ''
                  }
                  className={styles.answerText}
                  components={components}
                /> : <ReactMarkdown
                  linkTarget="_blank"
                  remarkPlugins={[remarkGfm, supersub]}
                  children={
                    SANITIZE_ANSWER
                      ? DOMPurify.sanitize(parsedAnswer.markdownFormatText ?? '', { ALLOWED_TAGS: XSSAllowTags })
                      : parsedAnswer.markdownFormatText ?? ''
                  }
                  className={styles.answerText}
                  components={components}
                />}
              </Stack.Item>
          </Stack>
        </Stack.Item>

        <Stack horizontal className={styles.answerFooter}>
          {!!parsedAnswer.citations.length && (
            <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
              <Stack className='referencesStack' style={{ width: '100%' }}>
                <Stack horizontal horizontalAlign="start" verticalAlign="center">
                  <Text
                    className={styles.accordionTitle}
                    onClick={toggleIsRefAccordionOpen}
                    aria-label="Open references"
                    tabIndex={0}
                    role="button">
                    <span>
                      {parsedAnswer.citations.length > 1
                        ? parsedAnswer.citations.length + ' references'
                        : '1 reference'}
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    onClick={handleChevronClick}
                    iconName={chevronIsExpanded ? 'ChevronDown' : 'ChevronRight'}
                  />
                </Stack>
                </Stack>
              </Stack.Item>
            )}
            {answer.MessageCategory != "greeting" && (
              <Stack.Item className={styles.answerDisclaimerContainer}>
                <span className={styles.answerDisclaimer}>AI-generated content may be incorrect</span>
              </Stack.Item>
            )}
          </Stack>
          {chevronIsExpanded && (agentHasFeature(FeatureFlag.RichReferences, selectedAgent) ? (
            <TrafoChatReferences 
              citations={parsedAnswer.citations} 
              onShowClick={(citation) => downloadFileFromPath(citation)}
              onTitleClick={(citation) => onCitationClicked(citation)}
            />
          ) : (
            <GeneralReferences 
                citations={parsedAnswer.citations} 
                onDownloadClick={(citation) => downloadFileFromPath(citation)}
                onReferenceClick={(citation) => onCitationClicked(citation)}
            />
          ))}
      </Stack>
      {}
        <Stack horizontal className={styles.feedbackContainer}>
          {FEEDBACK_ENABLED && answer.message_id !== undefined && answer.MessageCategory != "greeting" && (
            <Stack horizontal horizontalAlign="space-between">
              
              <ThumbLike20Filled
                aria-hidden="false"
                aria-label="Like this response"
                onClick={() => onLikeResponseClicked()}
                style={{
                  color:
                    feedbackState === Feedback.Positive ||
                    appStateContext?.state.feedbackState[answer.message_id] === Feedback.Positive
                      ? 'darkgreen' // zaznaczone
                      : (selectedAgent==="CCRP"||selectedAgent==="ccrp")?'green':'slategray', // niezaznaczone, ale zawsze zielone
                  cursor: 'pointer',
                  fontWeight:
                    feedbackState === Feedback.Positive ||
                    appStateContext?.state.feedbackState[answer.message_id] === Feedback.Positive
                      ? 'bold'
                      : 'normal',
                }}
              />

              <ThumbDislike20Filled
                aria-hidden="false"
                aria-label="Dislike this response"
                onClick={() => onDislikeResponseClicked()}
                style={{
                  color:
                    feedbackState !== Feedback.Positive &&
                    feedbackState !== Feedback.Neutral &&
                    feedbackState !== undefined
                      ? 'darkred' // zaznaczone
                      : (selectedAgent==="CCRP"||selectedAgent==="ccrp")?'red':'slategray', // niezaznaczone, ale zawsze czerwone
                  cursor: 'pointer',
                  fontWeight:
                    feedbackState !== Feedback.Positive &&
                    feedbackState !== Feedback.Neutral &&
                    feedbackState !== undefined
                      ? 'bold'
                      : 'normal',
                }}
              />

              {showcopiedStatus ? (
                <CheckmarkRegular
                  style={{
                    height: '20px'
                    , width: '20px', color: 'green', 
                    cursor: 'pointer',
                  }}
                />
              ) : (
                <Copy20Filled
                  onClick={onCopyMessageClicked}
                  style={{
                    color: 'slategray', // Default color for the copy icon
                    cursor: 'pointer',
                  }}
                />
              )}
              <CommandBarButton
                text="Export"
                menuProps={exportOptions(messages, answer, setIsBlinking)}
                styles={{ root: { color: 'slategray', cursor: 'pointer' } }}
                className={isBlinking ? styles.blinking : ''}
              />


            </Stack>

          )}
        </Stack>
      </Stack>
      <Dialog
        onDismiss={() => {
          resetFeedbackDialog();
          setFeedbackState(Feedback.Neutral);
        }}
        hidden={!isFeedbackDialogOpen}
        styles={{
          main: [
            {
              selectors: {
                ['@media (min-width: 480px)']: {
                  maxWidth: '600px',
                  background: '#FFFFFF',
                  boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  maxHeight: '600px',
                  minHeight: '100px'
                }
              }
            }
          ]
        }}
        dialogContentProps={{
          title: 'Submit Feedback',
          showCloseButton: true
        }}>
        <Stack tokens={{ childrenGap: 4 }}>
          <div>Your feedback will improve this experience.</div>
          {!showReportInappropriateFeedback ? <UnhelpfulFeedbackContent /> : <ReportInappropriateFeedbackContent />}
          <div>By pressing submit, your feedback will be visible to the application owner.</div>
          <DefaultButton disabled={negativeFeedbackList.length < 1} onClick={onSubmitNegativeFeedback}>
            Submit
          </DefaultButton>
        </Stack>
      </Dialog>
    </>
  );
}
